import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Keyboard, BookOpen, PlayCircle, FileText, HelpCircle,
  ExternalLink, ChevronRight
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import HelpIcon from '@/components/HelpIcon';

export default function ResourcesPage() {
  const navigate = useNavigate();
  
  const moodBoardShortcuts = [
    { category: 'General', shortcuts: [
      { keys: ['Ctrl', 'S'], action: 'Save mood board' },
      { keys: ['Esc'], action: 'Deselect / Close dialogs' },
      { keys: ['?'], action: 'Show keyboard shortcuts' },
      { keys: ['G'], action: 'Toggle grid overlay' },
    ]},
    { category: 'Add Elements', shortcuts: [
      { keys: ['V'], action: 'Select tool' },
      { keys: ['T'], action: 'Add text element' },
      { keys: ['R'], action: 'Add rectangle' },
      { keys: ['E'], action: 'Add ellipse' },
    ]},
    { category: 'Selected Element', shortcuts: [
      { keys: ['Delete'], action: 'Delete element' },
      { keys: ['Ctrl', 'D'], action: 'Duplicate element' },
      { keys: ['↑', '↓', '←', '→'], action: 'Nudge by 1px' },
      { keys: ['Shift', '↑↓←→'], action: 'Nudge by 10px' },
      { keys: [']'], action: 'Bring forward' },
      { keys: ['['], action: 'Send backward' },
    ]},
  ];
  
  const workflowGuides = [
    {
      title: 'Creating a Project',
      description: 'Learn how to set up a new project, add rooms, and configure project details.',
      steps: [
        'From the Dashboard, click "New Project" or visit the Projects page',
        'Enter project name, client information, and address',
        'Add rooms/spaces to define the project scope',
        'Upload floor plans if available',
        'Set the project phase and status'
      ]
    },
    {
      title: 'Using the Mood Board Editor',
      description: 'Create professional design presentations with images, shapes, and specifications.',
      steps: [
        'Navigate to a project and click "Mood Boards"',
        'Click "New Mood Board" or open an existing one',
        'Add images from your clips library or upload new ones',
        'Use the toolbar to add text, shapes, and icons',
        'Apply the cutout tool to isolate products from backgrounds',
        'Toggle SpecNum tags and legends for professional documentation',
        'Export as PNG for client presentations'
      ]
    },
    {
      title: 'Managing Clips (Spec Sheets)',
      description: 'Capture and organize product specifications from the web.',
      steps: [
        'Install the KEY-MADE browser extension',
        'Visit a product page on any vendor website',
        'Click the extension icon and clip the product',
        'Assign the clip to a project and room',
        'Add or edit specifications, pricing, and notes',
        'View clips in the library or within projects'
      ]
    },
    {
      title: 'Creating Estimates',
      description: 'Build detailed cost estimates with line items and assemblies.',
      steps: [
        'Navigate to a project and click "Estimates"',
        'Create a new estimate or continue an existing one',
        'Add line items manually or from project clips',
        'Organize items into groups and categories',
        'Add markups, taxes, and adjustments',
        'Export to PDF or Excel for proposals'
      ]
    },
  ];
  
  return (
    <div className="min-h-screen bg-[#F8F7F5]" data-testid="resources-page">
      {/* Header */}
      <header className="bg-white border-b border-border sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-6 py-4">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard')}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              My Desk
            </Button>
            <div className="h-6 w-px bg-border" />
            <div className="flex items-center gap-2">
              <HelpIcon elementId="resources-page" position="inline" size="md" />
              <BookOpen className="w-5 h-5 text-accent" />
              <h1 className="text-xl font-heading text-foreground">Resources & Help</h1>
            </div>
          </div>
        </div>
      </header>
      
      {/* Main Content */}
      <main className="max-w-5xl mx-auto px-6 py-8">
        <Tabs defaultValue="shortcuts" className="space-y-6">
          <TabsList className="bg-white p-1 shadow-sm">
            <TabsTrigger value="shortcuts" className="gap-2">
              <Keyboard className="w-4 h-4" />
              Keyboard Shortcuts
            </TabsTrigger>
            <TabsTrigger value="guides" className="gap-2">
              <FileText className="w-4 h-4" />
              Workflow Guides
            </TabsTrigger>
            <TabsTrigger value="videos" className="gap-2">
              <PlayCircle className="w-4 h-4" />
              Training Videos
            </TabsTrigger>
            <TabsTrigger value="support" className="gap-2">
              <HelpCircle className="w-4 h-4" />
              Get Help
            </TabsTrigger>
          </TabsList>
          
          {/* Keyboard Shortcuts Tab */}
          <TabsContent value="shortcuts">
            <Card className="bg-white border-none shadow-sm">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Keyboard className="w-5 h-5 text-accent" />
                  Mood Board Keyboard Shortcuts
                </CardTitle>
                <CardDescription>
                  Use these shortcuts to work faster in the Mood Board editor. Press <kbd className="px-1.5 py-0.5 bg-muted rounded text-xs font-mono">?</kbd> while in the editor to see these shortcuts.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-3 gap-8">
                  {moodBoardShortcuts.map((section) => (
                    <div key={section.category}>
                      <h3 className="font-medium text-sm text-muted-foreground mb-4 uppercase tracking-wide">
                        {section.category}
                      </h3>
                      <div className="space-y-3">
                        {section.shortcuts.map((shortcut, idx) => (
                          <div key={idx} className="flex items-center justify-between">
                            <span className="text-sm">{shortcut.action}</span>
                            <div className="flex gap-1">
                              {shortcut.keys.map((key, keyIdx) => (
                                <kbd key={keyIdx} className="px-2 py-1 bg-muted rounded text-xs font-mono min-w-[24px] text-center">
                                  {key}
                                </kbd>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-gradient-to-br from-accent/10 to-accent/5 border-none shadow-sm mt-6">
              <CardContent className="pt-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-accent/20 flex items-center justify-center flex-shrink-0">
                    <HelpCircle className="w-5 h-5 text-accent" />
                  </div>
                  <div>
                    <h3 className="font-medium mb-1">Pro Tip: Training Mode</h3>
                    <p className="text-sm text-muted-foreground">
                      Enable Training Mode in the Mood Board editor by clicking the &quot;Help&quot; button. 
                      This shows helpful tooltips and explanations for every tool and feature.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
          
          {/* Workflow Guides Tab */}
          <TabsContent value="guides">
            <div className="space-y-4">
              {workflowGuides.map((guide, idx) => (
                <Card key={idx} className="bg-white border-none shadow-sm">
                  <CardHeader>
                    <CardTitle className="text-lg">{guide.title}</CardTitle>
                    <CardDescription>{guide.description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ol className="space-y-2">
                      {guide.steps.map((step, stepIdx) => (
                        <li key={stepIdx} className="flex gap-3 text-sm">
                          <span className="w-6 h-6 rounded-full bg-accent/10 text-accent flex items-center justify-center flex-shrink-0 text-xs font-medium">
                            {stepIdx + 1}
                          </span>
                          <span className="text-muted-foreground pt-0.5">{step}</span>
                        </li>
                      ))}
                    </ol>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>
          
          {/* Training Videos Tab */}
          <TabsContent value="videos">
            <Card className="bg-white border-none shadow-sm">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <PlayCircle className="w-5 h-5 text-accent" />
                  Training Videos
                </CardTitle>
                <CardDescription>
                  Video tutorials to help you get the most out of KEY-MADE.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-4">
                  {[
                    { title: 'Getting Started with KEY-MADE', duration: '5 min', category: 'Basics' },
                    { title: 'Creating Your First Project', duration: '8 min', category: 'Projects' },
                    { title: 'Mood Board Mastery', duration: '12 min', category: 'Mood Boards' },
                    { title: 'Using the Web Clipper', duration: '6 min', category: 'Clips' },
                    { title: 'Building Estimates', duration: '10 min', category: 'Estimates' },
                    { title: 'Advanced Cutout Techniques', duration: '7 min', category: 'Mood Boards' },
                  ].map((video, idx) => (
                    <div 
                      key={idx}
                      className="flex items-center gap-4 p-4 bg-muted/50 rounded-lg hover:bg-muted cursor-pointer transition-colors"
                    >
                      <div className="w-16 h-12 rounded bg-secondary flex items-center justify-center">
                        <PlayCircle className="w-6 h-6 text-white" />
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-sm">{video.title}</p>
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          <span>{video.duration}</span>
                          <span>•</span>
                          <span>{video.category}</span>
                        </div>
                      </div>
                      <ChevronRight className="w-4 h-4 text-muted-foreground" />
                    </div>
                  ))}
                </div>
                <p className="text-sm text-muted-foreground text-center mt-6">
                  More videos coming soon!
                </p>
              </CardContent>
            </Card>
          </TabsContent>
          
          {/* Support Tab */}
          <TabsContent value="support">
            <div className="grid md:grid-cols-2 gap-6">
              <Card className="bg-white border-none shadow-sm">
                <CardHeader>
                  <CardTitle>Contact Support</CardTitle>
                  <CardDescription>
                    Need help? Our team is here to assist you.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
                    <div className="w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center">
                      <HelpCircle className="w-5 h-5 text-accent" />
                    </div>
                    <div>
                      <p className="font-medium text-sm">Email Support</p>
                      <p className="text-sm text-muted-foreground">support@keystonecreativeco.com</p>
                    </div>
                  </div>
                  <Button className="w-full" variant="outline">
                    <ExternalLink className="w-4 h-4 mr-2" />
                    Visit Help Center
                  </Button>
                </CardContent>
              </Card>
              
              <Card className="bg-white border-none shadow-sm">
                <CardHeader>
                  <CardTitle>Feature Requests</CardTitle>
                  <CardDescription>
                    Have an idea to improve KEY-MADE? We&apos;d love to hear it.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground mb-4">
                    KEY-MADE is continuously evolving based on user feedback. 
                    Share your ideas for new features or improvements.
                  </p>
                  <Button className="w-full" variant="outline">
                    Submit Feature Request
                  </Button>
                </CardContent>
              </Card>
            </div>
            
            <Card className="bg-gradient-to-br from-secondary/10 to-secondary/5 border-none shadow-sm mt-6">
              <CardContent className="pt-6">
                <div className="text-center">
                  <h3 className="font-heading text-lg mb-2">KEY-MADE</h3>
                  <p className="text-sm text-muted-foreground mb-1">
                    Managing Architectural Design Execution
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Version 1.0 • Built by KEY-MADE
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
